import type { RequestOptions } from "../common/server";
import type { IdOrFilesystemId } from "./types";
import type { Action, AddHotspotRequest, CreateMasterJs, CreateMasterRequest, DeleteArtboardRequest, DeleteArtboardsRequest, DeleteHotspotRequest, DeviceDropDownOption, DeviceType, EditHotspotRequest, ExpoStaticContent, HotspotAddResponse, HotspotDeleteResponse, HotspotEditResponse, InsertLocation, ModifyMasterRequest, Platform, Project, ReorderArtboardRequest, ResponseObject, SetHeaderFooterHeightRequest, ShortcutCreateJs, UpdatePlatformRequest } from "@/generated/models";
import { supportsUploadProgress } from "@/common/lib/browser";
import { objectToFormData } from "@/services/utils/formData";
import { cloudApiUrl } from "../urls";
import { server } from "./server";

export type CreateModel = IdOrFilesystemId & { Name?: string; Platform?: DeviceType; insertLocation?: InsertLocation; PublishNoteRichText?: string; insertAfter?: string };

export async function create(model: CreateModel) {
  const formData = objectToFormData(model);
  const response = await server.post(`expo/create`, { body: formData });
  return await response.json() as ShortcutCreateJs;
}

export async function addArtboard(
  { shortcut, files, skipPublishNotes }: { shortcut: string; files: File[] | File; skipPublishNotes?: boolean },
  { signal, onUploadProgress }: RequestOptions = {},
) {
  const model = {
    fileData: files,
    skipPublishNote: skipPublishNotes,
  };
  const formData = objectToFormData(model);
  const response = await server.post(`expo/addArtboard/${shortcut}`, {
    body: formData,
    signal,
    onUploadProgress: supportsUploadProgress(cloudApiUrl) ? onUploadProgress : undefined,
  });
  return await response.json() as ResponseObject;
}

export async function setZoom(
  { shortcut, zoom }: { shortcut: string; zoom: number },
) {
  const model = {
    zoom,
  };
  const response = await server.post(`expo/setZoom/${shortcut}`, { json: model });
  return await response.json() as ResponseObject;
}

export async function getProject(
  shortcut: string,
  { signal }: RequestOptions = {},
) {
  const response = await server.get(`expo/getProject/${shortcut}`, { signal });
  return await response.json() as Project;
}

export async function getStaticContent(
  shortcut: string,
  { signal }: RequestOptions = {},
) {
  const response = await server.get(`expo/getStaticContent/${shortcut}`, { signal });
  return await response.json() as ExpoStaticContent;
}

export async function getDeviceList(
  { signal }: RequestOptions = {},
) {
  const response = await server.get(`expo/getDeviceList`, { signal });
  return await response.json() as DeviceDropDownOption[];
}

export async function updatePlatform(shortcut: string, platform: Platform) {
  const data: UpdatePlatformRequest = { platform };
  return server.post(`expo/updatePlatform/${shortcut}`, { json: data });
}

export async function addHotspot(shortcut: string, artboardId: string, action: Action) {
  const data: AddHotspotRequest = {
    tag: "",
    hotspot: action.Hotspot,
    setting: action.Setting,
    type: action.Type,
    artboardId: action.IsMaster ? undefined : artboardId,
    masterId: action.IsMaster ? action.ParentId : undefined,
    relativeToBottom: action.RelativeToBottom,
  };

  const response = await server.post(`expo/addHotspot/${shortcut}`, { json: data });
  return await response.json() as HotspotAddResponse;
}

export async function editHotspot(shortcut: string, originalAction: Action, action: Action) {
  const data: EditHotspotRequest = {
    sourceId: originalAction.ParentId,
    targetId: action.ParentId,
    actionId: action.Id,
    hotspot: action.Hotspot,
    setting: action.Setting,
    type: action.Type,
    relativeToBottom: action.RelativeToBottom,
    isSourceMaster: originalAction.IsMaster,
    isTargetMaster: action.IsMaster,
  };

  const response = await server.post(`expo/editHotspot/${shortcut}`, { json: data });
  return await response.json() as HotspotEditResponse;
}

export async function deleteHotspot(shortcut: string, isMaster: boolean, sourceId: string, actionId: string) {
  const data: DeleteHotspotRequest = {
    isMaster,
    sourceId,
    actionId,
  };

  const response = await server.post(`expo/deleteHotspot/${shortcut}`, { json: data });
  return await response.json() as HotspotDeleteResponse;
}

export async function createMaster({ shortcut, name, artboardId }: { shortcut: string; name: string; artboardId: string }) {
  const data: CreateMasterRequest = {
    artboardId,
    name,
  };

  const response = await server.post(`expo/createMaster/${shortcut}`, { json: data });
  return await response.json() as CreateMasterJs;
}

export async function renameMaster({ shortcut, masterId, name }: { shortcut: string; masterId: string; name: string }) {
  const data = {
    masterId,
    name,
  };
  const response = await server.post(`expo/renameMaster/${shortcut}`, { json: data });
  return await response.json() as CreateMasterJs;
}

export async function copyMaster({ shortcut, masterId }: { shortcut: string; masterId: string }) {
  const data = {
    masterId,
  };
  const response = await server.post(`expo/copyMaster/${shortcut}`, { json: data });
  return await response.json() as CreateMasterJs;
}

export async function deleteMaster({ shortcut, masterId }: { shortcut: string; masterId: string }) {
  const data = {
    masterId,
  };
  const response = await server.post(`expo/deleteMaster/${shortcut}`, { json: data });
  return await response.json() as ResponseObject;
}

export async function addArtboardMaster(
  shortcut: string,
  artboardId: string,
  masterId: string,
) {
  const data: ModifyMasterRequest = {
    artboardId,
    masterId,
    name: "",
  };

  const response = await server.post(`expo/addMaster/${shortcut}`, { json: data });
  return await response.json() as ResponseObject;
}

export async function removeArtboardMaster(
  shortcut: string,
  artboardId: string,
  masterId: string,
) {
  const data: ModifyMasterRequest = {
    artboardId,
    masterId,
    name: "",
  };

  const response = await server.post(`expo/removeMaster/${shortcut}`, { json: data });
  return await response.json() as ResponseObject;
}

export async function deleteArtboard(shortcut: string, artboardId: string) {
  const data: DeleteArtboardRequest = { artboardId };

  const response = await server.post(`expo/deleteArtboard/${shortcut}`, { json: data });
  return await response.json() as ResponseObject;
}

export async function deleteArtboards(shortcut: string, artboardIds: string[]) {
  const data: DeleteArtboardsRequest = { artboardIds };

  const response = await server.post(`expo/deleteArtboards/${shortcut}`, { json: data });
  return await response.json() as ResponseObject;
}

export async function reorderArtboard(
  { shortcut, artboardId, tailArtboardId }: { shortcut: string; artboardId: string; tailArtboardId: string },
) {
  const data: ReorderArtboardRequest = {
    targetAbId: artboardId,
    tailAbId: tailArtboardId,
    fullResequence: false,
  };

  const response = await server.post(`expo/reorderArtboard/${shortcut}`, { json: data });
  return await response.json() as ResponseObject;
}

export async function setHeaderFooterHeight({
  shortcut,
  artboardId,
  height,
  isHeader,
}: { shortcut: string; artboardId: string; height: number; isHeader: boolean }) {
  const data: SetHeaderFooterHeightRequest = {
    artboardId,
    height,
    isHeader,
  };

  const response = await server.post(`expo/setHeaderFooterHeight/${shortcut}`, { json: data });
  return await response.json() as ResponseObject;
}
